import React from 'react';
import Script from 'next/script';
import Txt from 'components/atoms/Txt/Txt';
import classes from './PrivacyPromise.module.scss';
import A from 'components/atoms/A/A';
import Image from 'components/atoms/Image/Image';
import Notice from 'components/atoms/Notice/Notice';
import { FAQ } from 'components/pages/pages';
import privacyImg1 from './assets/privacy_img_1.png';
import privacyImg2 from './assets/privacy_img_2.png';
import StyledList from 'components/atoms/StyledList/StyledList';
import useLocalisedImage from 'hooks/useLocalisedImage';
import SplitRow from 'components/organisms/SplitRow/SplitRow';
import FullWidthCta from 'components/organisms/FullWidthCta/FullWidthCta';
import TrustpilotWidget from 'components/organisms/TrustpilotWidget/TrustpilotWidget';

export interface Props {}

const ContactUs: React.FC<Props> = () => {
  const localisedImage = useLocalisedImage();

  return (
    <main className="fadedPageBg">
      <Script
        type="text/javascript"
        src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
      />

      <SplitRow
        mobileColumnOrderSwapped
        contentAlignedTop
        isFirstRow
        leftColumn={
          <>
            <Txt component="h1" className={classes.mainTitle}>
              The Qmee Privacy Promise
            </Txt>
            <Txt component="h3" className={classes.subTitle}>
              The Qmee products are designed to protect your privacy.
            </Txt>

            <Txt component="p">
              Here at Qmee, protecting your data is at the top of our list of priorities. This means
              we look to be transparent with you about the information we store, as well as how we
              use it - giving you the opportunity to make changes to your details, or request to
              delete them.
            </Txt>

            <Txt component="p">
              We will regularly review this promise and update it when necessary. It&apos;s a good
              idea to check this page from time to time to ensure that you are happy with any
              changes that are made.
            </Txt>
          </>
        }
        rightColumn={<Image src={privacyImg1} alt="The Qmee Privacy Promise" />}
      />

      <div className={classes.darkBackground}>
        <SplitRow
          rowTitle="We Promise:"
          leftColumn={
            <>
              <StyledList listStyle="promise">
                <Txt component="li">To be open and honest about the information we collect.</Txt>
                <Txt component="li">To use it only for specific purposes we’ve told you about.</Txt>
                <Txt component="li">To protect your privacy and your information.</Txt>
                <Txt component="li">To explain your privacy rights and how to exercise them.</Txt>
              </StyledList>
            </>
          }
          rightColumn={
            <StyledList listStyle="promise">
              <Txt component="li">To collect no more than we need for those purposes.</Txt>
              <Txt component="li">To keep it for no longer than we need it.</Txt>
              <Txt component="li">To respond quickly to any concerns you raise with us.</Txt>
              <Txt component="li">
                To treat you and your information fairly, lawfully and transparently.
              </Txt>
            </StyledList>
          }
        />
      </div>

      <SplitRow
        rightColumn={
          <>
            <Txt component="h2">Still unsure?</Txt>

            <Txt component="p">
              If you&apos;re still wondering about Qmee, why not read about what our seasoned Qmee
              users have to say about us in our{' '}
              <A to="/blog/meet-our-users">&lsquo;Meet Our Qmee Users&rsquo;</A> blogs. Or, if
              you&apos;re looking for reviews at a glance, head to our{' '}
              <A to="https://www.trustpilot.com/review/qmee.com" target="_blank">
                <Txt>Trustpilot page</Txt>
              </A>
            </Txt>

            <TrustpilotWidget showSmallWidget />
          </>
        }
        leftColumn={<Image src={privacyImg2} alt="Still unsure?" />}
      />

      <FullWidthCta
        title={<Txt>Frequently Asked Questions</Txt>}
        ctaText="FAQ's"
        ctaLink={FAQ.path}
      >
        Check out our FAQ&apos;s to help with any unanswered questions
      </FullWidthCta>
    </main>
  );
};

export default ContactUs;
